'use client';

import { useState } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Play } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

type WatchVideoPillSource =
  | {
      /** YouTube video id, e.g. "IP-fRuHCK0M". */
      videoId: string;
      src?: never;
      thumbnailSrc?: never;
    }
  | {
      videoId?: never;
      /**
       * Path under /public to a self-hosted .mp4. Use this instead of videoId
       * when the film only exists as a local asset — the Arabic cuts don't go
       * up on YouTube, so they have no video id to embed.
       */
      src: string;
      /** Path under /public to the 16:9 still for the trigger thumbnail. */
      thumbnailSrc: string;
    };

type WatchVideoPillProps = WatchVideoPillSource & {
  /** Visible label, e.g. "Watch the story". */
  label: string;
  /** Human duration, e.g. "1:24". */
  duration: string;
  /** Accessible / modal title. */
  title: string;
  /** Colour scheme for the trigger text. Defaults to dark backgrounds. */
  tone?: 'dark' | 'light';
  /** Put the thumbnail to the right of the label instead of the left. */
  reverse?: boolean;
  className?: string;
};

export function WatchVideoPill({
  videoId,
  src,
  thumbnailSrc,
  label,
  duration,
  title,
  tone = 'dark',
  reverse = false,
  className,
}: WatchVideoPillProps) {
  const [open, setOpen] = useState(false);
  // mqdefault is a clean 16:9 frame (no letterbox bars, unlike hqdefault).
  const thumbnail = videoId
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : thumbnailSrc;
  const { locale } = useTranslation();
  const isDark = tone === 'dark';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={`${locale === 'ar' ? 'مشاهدة' : 'Watch'}: ${title} (${duration})`}
          className={cn(
            'group flex h-12 items-center gap-2.5 rounded-full py-1.5 transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2',
            reverse ? 'flex-row-reverse pr-1.5 pl-2' : 'pr-2 pl-1.5',
            isDark
              ? 'text-white focus-visible:ring-white/40'
              : 'text-neutral-800 focus-visible:ring-neutral-300',
            className,
          )}
        >
          <span className="radius-8 relative block h-9 w-16 shrink-0 overflow-hidden bg-neutral-800">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={thumbnail}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.06]"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/10">
              <Play className="h-3.5 w-3.5 fill-current text-white drop-shadow-sm" />
            </span>
          </span>

          <span className="whitespace-nowrap text-base font-medium">
            {label}{' '}
            <span className={isDark ? 'text-white/45' : 'text-neutral-400'}>
              ({duration})
            </span>
          </span>
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(94vw,960px)] -translate-x-1/2 -translate-y-1/2',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
            'duration-200 ease-out',
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-2xl ring-1 ring-white/10">
            {open &&
              (videoId ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                  title={title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                // Self-hosted cut. The dialog only mounts this on open, so the
                // file is never fetched until someone actually asks for it.
                <video
                  className="absolute inset-0 h-full w-full"
                  src={src}
                  poster={thumbnailSrc}
                  title={title}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                />
              ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
